export type PlanId = 'pilot' | 'copilot' | 'autopilot' | 'command'

export type Plan = {
  id: PlanId
  tier: string
  name: string
  price: string
  purpose: string
  popular?: boolean
  headerClass: string
  bandClass: string
  subtleText: string
}

// A cell is either included (true), not included (false), or a specific value.
export type FeatureValue = boolean | string

export type Feature = {
  name: string
  note?: string
  values: Record<PlanId, FeatureValue>
}

export type FeatureCategory = {
  title: string
  features: Feature[]
}

export const plans: Plan[] = [
  {
    id: 'pilot',
    tier: 'Entry',
    name: 'Pilot',
    price: '$615',
    purpose: 'Get the foundation right. Site, local SEO, phone and AI support.',
    headerClass: 'bg-red-950 text-white',
    bandClass: 'bg-red-950 text-white',
    subtleText: 'text-red-200',
  },
  {
    id: 'copilot',
    tier: 'Starter',
    name: 'Co-Pilot',
    price: '$1,235',
    purpose: 'Start driving demand. Paid ads, social and content added on top.',
    headerClass: 'bg-red-800 text-white',
    bandClass: 'bg-red-800 text-white',
    subtleText: 'text-red-100',
  },
  {
    id: 'autopilot',
    tier: 'Growth',
    name: 'Autopilot',
    price: '$2,780',
    purpose: 'Never miss a lead. Dedicated AdROI line, estimates, full pipelines.',
    popular: true,
    headerClass: 'bg-red-600 text-white',
    bandClass: 'bg-red-600 text-white',
    subtleText: 'text-red-50',
  },
  {
    id: 'command',
    tier: 'Full System',
    name: 'Command Center',
    price: '$5,355',
    purpose: 'The whole operation. White-label platform and advanced analytics.',
    headerClass: 'bg-gray-950 text-white',
    bandClass: 'bg-gray-950 text-white',
    subtleText: 'text-gray-400',
  },
]

export const onboardingFee = '$997'

export const featureCategories: FeatureCategory[] = [
  {
    title: 'Website',
    features: [
      {
        name: 'Custom website build',
        note: 'Built during onboarding',
        values: { pilot: true, copilot: true, autopilot: true, command: true },
      },
      {
        name: 'Hosting, SSL & maintenance',
        values: { pilot: true, copilot: true, autopilot: true, command: true },
      },
      {
        name: 'Content updates per month',
        values: { pilot: '2', copilot: '5', autopilot: '10', command: 'Unlimited' },
      },
      {
        name: 'Landing pages',
        values: { pilot: '1', copilot: '3', autopilot: '5', command: 'Unlimited' },
      },
    ],
  },
  {
    title: 'Local SEO',
    features: [
      {
        name: 'Google Business Profile optimization',
        values: { pilot: true, copilot: true, autopilot: true, command: true },
      },
      {
        name: 'Local citations & listings',
        values: { pilot: '50', copilot: '100', autopilot: '200', command: '300+' },
      },
      {
        name: 'Review generation',
        values: { pilot: true, copilot: true, autopilot: true, command: true },
      },
      {
        name: 'Tracked keywords',
        values: { pilot: '10', copilot: '25', autopilot: '50', command: '100' },
      },
    ],
  },
  {
    title: 'Paid Ads & Social',
    features: [
      {
        name: 'Google & Meta ads management',
        values: { pilot: false, copilot: true, autopilot: true, command: true },
      },
      {
        name: 'Monthly ad spend managed',
        note: 'Ad spend billed separately',
        values: { pilot: false, copilot: 'Up to $5k', autopilot: 'Up to $15k', command: 'Custom' },
      },
      {
        name: 'Social posts per month',
        values: { pilot: false, copilot: '8', autopilot: '16', command: '30' },
      },
      {
        name: 'Blog & content articles',
        values: { pilot: false, copilot: '2', autopilot: '4', command: '8' },
      },
    ],
  },
  {
    title: 'CRM & Phone',
    features: [
      {
        name: 'CRM & unified lead inbox',
        values: { pilot: true, copilot: true, autopilot: true, command: true },
      },
      {
        name: 'Business phone number & texting',
        values: { pilot: true, copilot: true, autopilot: true, command: true },
      },
      {
        name: 'Dedicated AdROI line',
        values: { pilot: false, copilot: false, autopilot: true, command: true },
      },
      {
        name: 'Estimates & invoicing',
        values: { pilot: false, copilot: false, autopilot: true, command: true },
      },
      {
        name: 'Sales pipelines & automations',
        values: { pilot: false, copilot: 'Basic', autopilot: 'Full', command: 'Full' },
      },
    ],
  },
  {
    title: 'AI & Support',
    features: [
      {
        name: 'AI chat & missed-call text-back',
        values: { pilot: true, copilot: true, autopilot: true, command: true },
      },
      {
        name: 'AI voice receptionist',
        values: { pilot: false, copilot: false, autopilot: true, command: true },
      },
      {
        name: 'Support',
        values: { pilot: 'Email', copilot: 'Email & chat', autopilot: 'Priority', command: 'Dedicated manager' },
      },
    ],
  },
  {
    title: 'Reporting',
    features: [
      {
        name: 'Monthly performance report',
        values: { pilot: true, copilot: true, autopilot: true, command: true },
      },
      {
        name: 'Live ROI dashboard',
        values: { pilot: false, copilot: true, autopilot: true, command: true },
      },
      {
        name: 'Advanced analytics & attribution',
        values: { pilot: false, copilot: false, autopilot: false, command: true },
      },
      {
        name: 'White-label platform',
        values: { pilot: false, copilot: false, autopilot: false, command: true },
      },
    ],
  },
]
